import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { OnboardingStepper } from "@/components/onboarding/onboarding-stepper";
import { CompanyDetailsStep } from "@/pages/onboarding/steps/company-details-step";
import OnboardingPage from "@/pages/app/user/onboarding";
import { OnboardingCheckout } from "@/components/onboarding/onboarding-checkout";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useDeleteCompany } from "@/hooks/company/use-company";
import { useCompanyStore } from "@/store/use-company-store";
import { useCreateCompanyFlow } from "@/hooks/create-company/use-create-company-flow";
import { clearProgress } from "@/utils/create-company-progress";
import { CreateCompanyHeader } from "@/components/create-company/create-company-header";
import { CreateCompanyCancelDialog } from "@/components/create-company/create-company-cancel-dialog";

const STEPS = [
  { number: 1, title: "Company Details", description: "Set up your company" },
  { number: 2, title: "Choose Plan", description: "Select subscription" },
  { number: 3, title: "Checkout", description: "Complete payment" },
];

export default function CreateCompanyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { clearCompany } = useCompanyStore();
  const deleteCompanyMutation = useDeleteCompany();
  const { currentStep, companyId, selectedPlanId, handleStepComplete } = useCreateCompanyFlow();

  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = async () => {
    const companyIdToDelete = companyId;

    clearProgress();
    localStorage.removeItem('pendingInvitationEmails');

    if (companyIdToDelete) {
      try {
        await deleteCompanyMutation.mutateAsync({
          companyId: companyIdToDelete,
          suppressToast: true,
        });
      } catch {
        // Continue cleanup even if delete fails
      }
    }

    clearCompany();
    queryClient.removeQueries({ queryKey: ['companyOnboardingStatus'] });
    queryClient.invalidateQueries({ queryKey: ['userMemberships'] });

    setShowCancelDialog(false);
    toast.success(t('company.create.cancelledSuccess') || 'Company creation cancelled successfully');
    navigate('/', { replace: true });
  };

  const completedSteps: number[] = [];
  if (currentStep > 1) completedSteps.push(1);
  if (currentStep > 2) completedSteps.push(2);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1480px] mx-auto">
        <CreateCompanyHeader
          onCancel={handleCancelClick}
          isLoading={deleteCompanyMutation.isPending}
        />

        <CreateCompanyCancelDialog
          open={showCancelDialog}
          onOpenChange={setShowCancelDialog}
          onConfirm={handleCancelConfirm}
          hasCompany={!!companyId}
          isDeleting={deleteCompanyMutation.isPending}
        />

        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <OnboardingStepper
            steps={STEPS}
            currentStep={currentStep}
            completedSteps={completedSteps}
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8">
          {currentStep === 1 && (
            <CompanyDetailsStep onComplete={handleStepComplete} isCreatingNew={true} />
          )}

          {currentStep === 2 && (
            companyId ? (
              <OnboardingPage onComplete={handleStepComplete} companyId={companyId} />
            ) : (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            )
          )}

          {currentStep === 3 && companyId && (selectedPlanId || searchParams.get('planId')) && (
            <OnboardingCheckout
              onComplete={handleStepComplete}
              onCancel={handleCancelClick}
              companyId={companyId}
              planId={selectedPlanId || searchParams.get('planId') || undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
}
